/**
 * <order-refunds> — the open order's own refund record, and the one surface in
 * the console that says a refund did not come back.
 *
 * It exists because the only window a failed refund ever had was the batch
 * dialog's live `0 done / 0 failed` counter, which was a projection of an
 * in-flight subscription and vanished with the overlay that held it. This is a
 * projection of documents instead: the same rows render on every open, at 9am
 * tomorrow as at 5pm today, with nobody having had to be watching. That is the
 * whole difference the incident turned on — not the overlay, the source.
 *
 * The seven `failure` tokens are never rendered. Each one's own consequence is,
 * in the words `firebase/OPERATIONS.md` uses for it, because the
 * operator's next act differs per token and the token itself says none of it:
 * whether money left, whether the books show it, whether the tickets still
 * admit at the door.
 *
 * Three ranks read differently on purpose (`order-refunds.state.ts` orders
 * them):
 *   - `failed` — money that did not come back with nobody working on it. The
 *     loudest thing on the order sheet: an error-tinted block, its verdict,
 *     what the money actually did, and what the row is holding.
 *   - `claiming` carrying a refusal — the ladder is still asking, and the
 *     provider has already said why it keeps refusing. Warning-tinted, and the
 *     only rank with hope in it: on `insufficient_funds` the whole remedy is
 *     topping the account up, and the next tick pays out with no gesture here
 *     at all. Same component, different state, because it IS the same document
 *     — one becomes the other by the passage of time, and an operator who
 *     learned two vocabularies for one object would read neither.
 *   - everything else — a receipt. Typography, no container, no icon. A ledger
 *     where every line shouted would have no alarm left to raise.
 *
 * The provider's own words are relayed verbatim and attributed, never
 * paraphrased and never dressed as ours: `message` is the single actionable
 * string in the whole incident ("There is not enough funds…"), it is English in
 * a German console, and it is quoted and `lang`-marked so it reads as Revolut
 * speaking rather than as copy this product wrote. `code` rides small and
 * secondary beneath it — it degrades to `bad_request` and carries no meaning of
 * its own, but it is what an operator pastes into a support thread.
 *
 * No control clears a row: that is a hand-written correction to a money
 * document and belongs to whoever owns the books. What this surface owes the
 * operator instead is the fact that would otherwise trip them — a failed row
 * keeps holding its `ticketIds`, so a fresh batch raised before the row is
 * cleared refuses and leaves a second stuck marker. Stated as a consequence of
 * what the row still holds, never as an instruction to go and do something this
 * surface cannot do.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import type { Refund } from "@momo/types";
import { css, html, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../../i18n/localize.ts";
import { formatCents } from "../order-detail.engine.ts";
import { orderRefundsView } from "./ledger.state.ts";

/** What each terminal reason means for the money, the tickets and the seats —
 *  `OPERATIONS.md`'s own "What happened" column, one catalog key per sentence.
 *  `holdsTickets` marks the reasons where nothing at all was recorded, which is
 *  exactly where a fresh batch would be refused by the row still standing; on
 *  a row whose tickets are already void, saying so would be noise. */
const FAILURE_COPY = {
	providerRefused: {
		verdict: "admin.events.console.orderRefunds.failedProviderRefused",
		money: [
			"admin.events.console.orderRefunds.moneyNothingMoved",
			"admin.events.console.orderRefunds.moneyCheckDashboard",
		],
		holdsTickets: true,
	},
	attemptsExhausted: {
		verdict: "admin.events.console.orderRefunds.failedAttemptsExhausted",
		money: [
			"admin.events.console.orderRefunds.moneyNothingMoved",
			"admin.events.console.orderRefunds.moneyCheckDashboard",
		],
		holdsTickets: true,
	},
	headroomLost: {
		verdict: "admin.events.console.orderRefunds.failedHeadroomLost",
		money: [
			"admin.events.console.orderRefunds.moneyNothingMoved",
			"admin.events.console.orderRefunds.moneyCheckDashboard",
		],
		holdsTickets: true,
	},
	redeemedAfterClaim: {
		verdict: "admin.events.console.orderRefunds.failedRedeemedAfterClaim",
		money: ["admin.events.console.orderRefunds.moneyDoorRecordStands"],
		holdsTickets: true,
	},
	settleRefused: {
		verdict: "admin.events.console.orderRefunds.failedSettleRefused",
		money: ["admin.events.console.orderRefunds.moneySettleRefused"],
		holdsTickets: true,
	},
	overpaid: {
		verdict: "admin.events.console.orderRefunds.failedOverpaid",
		money: ["admin.events.console.orderRefunds.moneyOverpaid"],
		holdsTickets: false,
	},
	unsettledPastBound: {
		verdict: "admin.events.console.orderRefunds.failedUnsettledPastBound",
		money: ["admin.events.console.orderRefunds.moneyUnsettled"],
		holdsTickets: false,
	},
} as const satisfies Record<
	NonNullable<Refund.Refund["failure"]>,
	{
		readonly verdict: string;
		readonly money: readonly string[];
		readonly holdsTickets: boolean;
	}
>;

/** The ladder running out is the one reason whose answer flips on a second
 *  field: a consumer caught between its provider call and its recording
 *  transaction records the money onto the row afterwards, so a
 *  `providerRefundId` here means the give-back landed and only the row's own
 *  status is left. */
const EXHAUSTED_BUT_LANDED = {
	verdict: "admin.events.console.orderRefunds.failedAttemptsExhaustedLanded",
	money: ["admin.events.console.orderRefunds.moneyLanded"],
	holdsTickets: false,
} as const;

/** The one refusal code whose remedy is entirely outside this console and needs
 *  no gesture in it. Every other code falls back to the generic ladder
 *  sentence, so a code Revolut renames degrades to that rather than to a wrong
 *  instruction. */
const INSUFFICIENT_FUNDS = "insufficient_funds";

/** Every catalog key a loud row can put in its body, as a union rather than
 *  `string`: the body is assembled per row from two tables plus three
 *  conditional sentences, and an array literal would otherwise widen away the
 *  literal types `t()` checks its argument against. */
type BodyKey =
	| (typeof FAILURE_COPY)[keyof typeof FAILURE_COPY]["money"][number]
	| (typeof EXHAUSTED_BUT_LANDED)["money"][number]
	| "admin.events.console.orderRefunds.holdsTicketsSingular"
	| "admin.events.console.orderRefunds.holdsTicketsPlural"
	| "admin.events.console.orderRefunds.retryingInsufficientFunds"
	| "admin.events.console.orderRefunds.retryingLadder";

@schmancyElement("order-refunds")
export class OrderRefunds extends LocalizeMixin(SchmancyElement) {
	/** `display: contents`, not `block`: the region renders nothing at all for
	 *  the great majority of orders, and a zero-height host inside the order
	 *  sheet's `gap-6` column would still take a 24px gap with it. */
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	];

	/** Which order this sheet is showing. The ledger cell is module-scoped and
	 *  one order at a time is loaded into it, so the guard below is the same one
	 *  order-detail.view.ts already makes against the refund draft. */
	@property({ type: String }) orderId = "";

	@state() private copyNotices: Readonly<Record<string, string>> = {};

	override render(): TemplateResult {
		const view = orderRefundsView.get();
		if (!view || view.orderId !== this.orderId || view.entries.length === 0)
			return html``;
		return html`
			<div class="flex flex-col gap-3">
				<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
					${this.t("admin.events.console.orderRefunds.title")}
				</schmancy-typography>
				${when(
					view.needsAPerson > 0,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-error-default">
							${this.t(
								view.needsAPerson === 1
									? "admin.events.console.orderRefunds.waitingSingular"
									: "admin.events.console.orderRefunds.waitingPlural",
								{ count: view.needsAPerson },
							)}
						</schmancy-typography>
					`,
				)}
				${repeat(
					view.entries,
					(entry) => entry.row.id,
					({ kind, row }) => {
						const params = {
							amount: formatCents(row.amount),
							unrecorded: formatCents(row.unrecordedAmount ?? 0),
							count: row.ticketIds.length,
							number: row.correctionNumber ?? "",
							date: row.settledAt
								? new Intl.DateTimeFormat(
										this.uiLanguage === "de" ? "de-DE" : "en-GB",
										{ day: "numeric", month: "long" },
									).format(row.settledAt.toDate())
								: "",
						};
						if (kind === "settled" || kind === "underWay") {
							// Only the two origins that mean "not made in this console" say
							// so; a member uid is a raw id and says nothing to a reader.
							const origin =
								row.initiatedBy === "customer"
									? this.t("admin.events.console.orderRefunds.originCustomer")
									: row.initiatedBy === "psp"
										? this.t("admin.events.console.orderRefunds.originPsp")
										: "";
							return html`
								<div class="flex flex-col gap-0.5">
									<schmancy-typography type="body" token="sm">
										${this.t(
											kind === "settled"
												? "admin.events.console.orderRefunds.settled"
												: row.status === "pending"
													? "admin.events.console.orderRefunds.pending"
													: "admin.events.console.orderRefunds.claiming",
											params,
										)}
									</schmancy-typography>
									<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
										${[
											this.t(
												row.ticketIds.length === 1
													? "admin.events.console.orderRefunds.settledTicketsSingular"
													: "admin.events.console.orderRefunds.settledTicketsPlural",
												params,
											),
											row.correctionNumber
												? this.t(
														"admin.events.console.orderRefunds.correction",
														params,
													)
												: "",
											origin,
										]
											.filter((part) => part !== "")
											.join(" · ")}
									</schmancy-typography>
								</div>
							`;
						}

						const alarm = kind === "needsAPerson";
						const copy = !alarm
							? null
							: row.failure === "attemptsExhausted" && row.providerRefundId
								? EXHAUSTED_BUT_LANDED
								: row.failure
									? FAILURE_COPY[row.failure]
									: null;
						const reference =
							alarm && row.batchId ? `${row.batchId}.${this.orderId}` : null;
						// Annotated rather than inferred: an array literal assembled from
						// two tables and three conditional sentences widens to string[],
						// and `t()` checks its argument against the catalog's own keys.
						const body: readonly BodyKey[] = alarm
							? [
									...(copy?.money ?? []),
									...(copy?.holdsTickets
										? [
												row.ticketIds.length === 1
													? ("admin.events.console.orderRefunds.holdsTicketsSingular" as const)
													: ("admin.events.console.orderRefunds.holdsTicketsPlural" as const),
											]
										: []),
								]
							: [
									row.providerRefusal?.code === INSUFFICIENT_FUNDS
										? "admin.events.console.orderRefunds.retryingInsufficientFunds"
										: "admin.events.console.orderRefunds.retryingLadder",
								];
						return html`
							<div
								class=${this.classMap({
									"flex flex-col gap-2 rounded-2xl p-4": true,
									"bg-error-container": alarm,
									"bg-warning-container": !alarm,
								})}
							>
								<div class="flex items-start gap-2">
									<schmancy-icon
										aria-hidden="true"
										class=${this.classMap({
											"text-error-onContainer": alarm,
											"text-warning-onContainer": !alarm,
										})}
										>${alarm ? "error" : "schedule"}</schmancy-icon
									>
									<schmancy-typography
										type="title"
										token="sm"
										class=${this.classMap({
											"min-w-0 flex-1": true,
											"text-error-onContainer": alarm,
											"text-warning-onContainer": !alarm,
										})}
									>
										${
											alarm
												? this.t(
														copy?.verdict ??
															"admin.events.console.orderRefunds.failedUnknown",
														params,
													)
												: this.t(
														"admin.events.console.orderRefunds.retryingHeadline",
														params,
													)
										}
									</schmancy-typography>
								</div>
								${repeat(
									body,
									(key) => key,
									(key) => html`
										<schmancy-typography
											type="body"
											token="sm"
											class=${this.classMap({
												"text-error-onContainer": alarm,
												"text-warning-onContainer": !alarm,
											})}
										>
											${this.t(key, params)}
										</schmancy-typography>
									`,
								)}
								${when(
									row.providerRefusal,
									(refusal) => html`
										<div class="flex flex-col gap-0.5">
											<schmancy-typography
												type="label"
												token="sm"
												class=${this.classMap({
													"text-error-onContainer": alarm,
													"text-warning-onContainer": !alarm,
												})}
											>
												${this.t("admin.events.console.orderRefunds.providerSaid")}
											</schmancy-typography>
											<schmancy-typography
												type="body"
												token="sm"
												lang="en"
												class=${this.classMap({
													"hyphens-auto wrap-break-word italic": true,
													"text-error-onContainer": alarm,
													"text-warning-onContainer": !alarm,
												})}
											>
												“${refusal.message}”
											</schmancy-typography>
											${when(
												refusal.code,
												(code) => html`
													<schmancy-typography
														type="label"
														token="sm"
														class=${this.classMap({
															"font-mono opacity-70": true,
															"text-error-onContainer": alarm,
															"text-warning-onContainer": !alarm,
														})}
														>${code}</schmancy-typography
													>
												`,
											)}
										</div>
									`,
								)}
								${when(
									reference,
									(key) => html`
										<div class="flex">
											<schmancy-button
												variant="outlined"
												color="error"
												@click=${() => {
													navigator.clipboard
														.writeText(key)
														.then(
															() =>
																(this.copyNotices = {
																	...this.copyNotices,
																	[key]: this.t(
																		"admin.events.console.orderRefunds.referenceCopied",
																	),
																}),
														)
														.catch(
															() =>
																(this.copyNotices = {
																	...this.copyNotices,
																	[key]: this.t(
																		"admin.events.console.orderRefunds.referenceCopyFailed",
																	),
																}),
														);
												}}
											>
												<schmancy-icon slot="prefix" aria-hidden="true">content_copy</schmancy-icon>
												${this.t("admin.events.console.orderRefunds.copyReference")}
											</schmancy-button>
                                                <schmancy-typography role="status" type="body" token="sm" class="h-12">${this.copyNotices[key] ?? ""}</schmancy-typography>
										</div>
									`,
								)}
							</div>
						`;
					},
				)}
				${when(
					view.needsAPerson > 0,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t("admin.events.console.orderRefunds.clearedByHand")}
						</schmancy-typography>
					`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"order-refunds": OrderRefunds;
	}
}
